import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { PublicProfile, UserProfile } from '../types/social';

const PROFILE_COLUMNS =
  'user_id, display_name, username, slug, album_visibility, show_in_fof_suggestions';

const PUBLIC_COLUMNS =
  'user_id, display_name, username, slug, album_visibility';

export function useMyProfile(userId: string | undefined) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: fetchErr } = await supabase
        .from('profiles')
        .select(PROFILE_COLUMNS)
        .eq('user_id', userId)
        .maybeSingle<UserProfile>();
      if (cancelled) return;
      if (fetchErr) {
        setError(fetchErr.message);
      } else if (data) {
        setProfile(data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const update = useCallback(
    async (
      patch: Partial<UserProfile>
    ): Promise<{ ok: true } | { ok: false; code?: string; message: string }> => {
      if (!userId || !profile) return { ok: false, message: 'no profile' };
      setSaving(true);
      setError(null);
      const next: UserProfile = { ...profile, ...patch };
      setProfile(next); // otimista
      const { error: upErr } = await supabase
        .from('profiles')
        .update(patch)
        .eq('user_id', userId);
      setSaving(false);
      if (upErr) {
        setError(upErr.message);
        setProfile(profile);
        return { ok: false, code: upErr.code, message: upErr.message };
      }
      setSavedAt(Date.now());
      return { ok: true };
    },
    [profile, userId]
  );

  return { profile, loading, saving, savedAt, error, update };
}

export function useProfileBySlug(slug: string | undefined) {
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      const { data, error: fetchErr } = await supabase
        .from('profiles_public')
        .select(PUBLIC_COLUMNS)
        .eq('slug', slug)
        .maybeSingle<PublicProfile>();
      if (cancelled) return;
      if (fetchErr || !data) {
        setNotFound(true);
        setProfile(null);
      } else {
        setProfile(data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { profile, loading, notFound };
}

export function useProfileByUserId(userId: string | undefined) {
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      const { data, error: fetchErr } = await supabase
        .from('profiles_public')
        .select(PUBLIC_COLUMNS)
        .eq('user_id', userId)
        .maybeSingle<PublicProfile>();
      if (cancelled) return;
      if (fetchErr || !data) {
        setNotFound(true);
        setProfile(null);
      } else {
        setProfile(data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { profile, loading, notFound };
}
